import { useTranslate } from "@tolgee/react";
import { PdfDownloadArgs } from "../quotes/hooks/useDownloadQuotePdf.js";
import { useDownloadQuotePdfOptions } from "../quotes/hooks/useDownloadQuotePdfOptions.js";
import { UseDownloadQuotePdfOptions_QuoteFragment } from "../quotes/hooks/useDownloadQuotePdfOptions.generated.js";

export type QuotePdfGenerationOption = {
  key: string;
  group: string;
  label: string;
  args: PdfDownloadArgs;
  /**
   * The letterhead assigned to this option's PDF generator template. Set on the
   * generator options only — no other path names a template that can hold one.
   * Used to show what would be composed; never sent.
   */
  pdfLetterheadId: string | null;
};

/**
 * The four generation paths flattened into one selectable list. The label is
 * also the default document name, because it is only assembled client-side and
 * differs per source.
 */
export function useQuotePdfGenerationOptions({
  doc,
}: {
  doc: UseDownloadQuotePdfOptions_QuoteFragment | undefined;
}): { options: QuotePdfGenerationOption[]; loading: boolean } {
  const { t } = useTranslate(["Quote"]);
  const {
    hasSelectedCalculations,
    contractingPdfs,
    sexyQuotePdfTemplates,
    pdfGeneratorTemplates,
    loading,
  } = useDownloadQuotePdfOptions({ doc });

  const agreed = t("agreed", { ns: "Quote" });
  const proposed = t("proposed", { ns: "Quote" });

  const options: QuotePdfGenerationOption[] = [];

  const quotePdfGroup = t("Quote PDF", { ns: "Quote" });
  if (hasSelectedCalculations) {
    options.push({
      key: "none-agreed",
      group: quotePdfGroup,
      label: `${quotePdfGroup} (${agreed})`,
      args: { templateType: "none", calculationType: "agreed" },
      pdfLetterheadId: null,
    });
  }
  options.push({
    key: "none-proposed",
    group: quotePdfGroup,
    label: hasSelectedCalculations
      ? `${quotePdfGroup} (${proposed})`
      : quotePdfGroup,
    args: { templateType: "none", calculationType: "proposed" },
    pdfLetterheadId: null,
  });

  const contractingGroup = t("Contracting PDF", { ns: "Quote" });
  for (const contractingPdf of contractingPdfs) {
    if (hasSelectedCalculations) {
      options.push({
        key: `contracting-agreed-${contractingPdf.id}`,
        group: contractingGroup,
        label: `${contractingPdf.title} (${agreed})`,
        args: {
          templateType: "contracting",
          calculationType: "agreed",
          contractingPdfId: contractingPdf.id,
        },
        pdfLetterheadId: null,
      });
    }
    options.push({
      key: `contracting-proposed-${contractingPdf.id}`,
      group: contractingGroup,
      label: hasSelectedCalculations
        ? `${contractingPdf.title} (${proposed})`
        : contractingPdf.title,
      args: {
        templateType: "contracting",
        calculationType: "proposed",
        contractingPdfId: contractingPdf.id,
      },
      pdfLetterheadId: null,
    });
  }

  const sexyGroup = t("Sexy PDF", { ns: "Quote" });
  for (const pdfTemplate of sexyQuotePdfTemplates) {
    options.push({
      key: `sexy-proposed-${pdfTemplate.pdfTemplateUrl}`,
      group: sexyGroup,
      label: pdfTemplate.label,
      args: {
        templateType: "sexy",
        calculationType: "proposed",
        pdfTemplateUrl: pdfTemplate.pdfTemplateUrl,
      },
      pdfLetterheadId: null,
    });
  }

  const generatorGroup = t("Generator PDF", { ns: "Quote" });
  for (const template of pdfGeneratorTemplates) {
    if (hasSelectedCalculations) {
      options.push({
        key: `generator-agreed-${template.id}`,
        group: generatorGroup,
        label: `${template.name} (${agreed})`,
        args: {
          templateType: "generator",
          calculationType: "agreed",
          pdfGeneratorTemplateId: template.id,
        },
        pdfLetterheadId: template.pdfLetterheadId ?? null,
      });
    }
    options.push({
      key: `generator-proposed-${template.id}`,
      group: generatorGroup,
      label: hasSelectedCalculations
        ? `${template.name} (${proposed})`
        : template.name,
      args: {
        templateType: "generator",
        calculationType: "proposed",
        pdfGeneratorTemplateId: template.id,
      },
      pdfLetterheadId: template.pdfLetterheadId ?? null,
    });
  }

  return { options, loading };
}
